import React, { useEffect, useState, useCallback } from 'react';
import api from '../../services/api';

export default function AdminAgents() {
  const [agents, setAgents] = useState([]);
  const [pending, setPending] = useState([]);
  const [queues, setQueues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [selected, setSelected] = useState(null);
  const [acting, setActing] = useState(null);

  const load = useCallback(async () => {
    const [agRes, pRes, qRes] = await Promise.all([
      api.get('/api/v1/agents/').catch(() => ({ data: [] })),
      api.get('/api/v1/agents/pending').catch(() => ({ data: [] })),
      api.get('/api/v1/agents/offline-queues').catch(() => ({ data: [] })),
    ]);
    setAgents(Array.isArray(agRes.data) ? agRes.data : []);
    setPending(Array.isArray(pRes.data) ? pRes.data : []);
    setQueues(Array.isArray(qRes.data) ? qRes.data : []);
    setLastRefresh(new Date());
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const interval = setInterval(load, 15000);
    return () => clearInterval(interval);
  }, [load]);

  const approve = async (agent) => {
    setActing(agent.id);
    try {
      await api.post(`/api/v1/agents/${agent.id}/approve`, { notes: '' });
      await load();
    } catch (e) {
      alert('No se pudo aprobar: ' + (e.response?.data?.detail || e.message));
    } finally { setActing(null); }
  };

  const reject = async (agent) => {
    const reason = window.prompt('Motivo del rechazo:');
    if (reason === null) return;
    if (!reason.trim()) { alert('Debes indicar un motivo.'); return; }
    setActing(agent.id);
    try {
      await api.post(`/api/v1/agents/${agent.id}/reject`, { reason: reason.trim() });
      await load();
    } catch (e) {
      alert('No se pudo rechazar: ' + (e.response?.data?.detail || e.message));
    } finally { setActing(null); }
  };

  const online = agents.filter(a => a.is_online);
  const offline = agents.filter(a => !a.is_online);

  const agentQueue = selected
    ? queues.filter(q => q.agent_id === selected.id)
    : [];

  return (
    <div style={{ display: selected ? 'grid' : 'block', gridTemplateColumns: '1fr 380px', gap: 18 }}>
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <h1 style={{ fontFamily: 'Georgia,serif', fontSize: 26, margin: '0 0 4px' }}>Agentes</h1>
            <p style={{ fontSize: 13, color: '#7a7060', margin: 0 }}>
              {pending.length} pendientes · {online.length} online · {offline.length} offline · Auto-refresh 15s
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <button onClick={load} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #e2dbd0', background: '#fff', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
              ↻ Actualizar
            </button>
            <div style={{ fontSize: 11, color: '#b0a090', marginTop: 4 }}>
              {lastRefresh.toLocaleTimeString('es-ES')}
            </div>
          </div>
        </div>

        {/* Solicitudes pendientes */}
        {pending.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#b45309', marginBottom: 10 }}>
              ⏳ Solicitudes pendientes ({pending.length})
            </div>
            {pending.map((p) => (
              <div key={p.id} style={{ background: '#fff', border: '1px solid #f0c99a', borderRadius: 12, padding: '16px 18px', marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 2 }}>{p.full_name}</div>
                    <div style={{ fontSize: 12, color: '#7a7060', marginBottom: 8 }}>
                      <span style={{ fontFamily: 'monospace', color: '#c94a1e' }}>{p.code}</span>
                      {' · '}{p.province}{p.city ? `, ${p.city}` : ''}{p.country ? ` · ${p.country}` : ''}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 12, color: '#5a5245' }}>
                      <span>📞 {p.phone || '—'}</span>
                      <span>✉️ {p.email || '—'}</span>
                      {p.telegram_username && <span>✈️ @{p.telegram_username}</span>}
                      <span>📦 {Array.isArray(p.delivery_methods) ? p.delivery_methods.join(', ') : '—'}</span>
                      <span>🎯 {p.max_tx_per_day}/día</span>
                    </div>
                    <div style={{ fontSize: 11, color: '#b0a090', marginTop: 6 }}>
                      Solicitado: {p.created_at ? new Date(p.created_at).toLocaleString('es-ES') : '—'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 110 }}>
                    <button
                      onClick={() => approve(p)}
                      disabled={acting === p.id}
                      style={{ padding: '9px 14px', borderRadius: 8, border: 'none', background: acting === p.id ? '#a8c9b8' : '#2d6a4f', color: '#fff', fontSize: 13, fontWeight: 700, cursor: acting === p.id ? 'default' : 'pointer' }}>
                      {acting === p.id ? '...' : '✓ Aprobar'}
                    </button>
                    <button
                      onClick={() => reject(p)}
                      disabled={acting === p.id}
                      style={{ padding: '9px 14px', borderRadius: 8, border: '1px solid #d9a5a5', background: '#fff', color: '#a32d2d', fontSize: 13, fontWeight: 700, cursor: acting === p.id ? 'default' : 'pointer' }}>
                      ✕ Rechazar
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
          {[
            { label: 'Online', value: online.length, color: '#2d6a4f', bg: '#eaf4ef' },
            { label: 'Offline', value: offline.length, color: '#7a7060', bg: '#f0ebe2' },
            { label: 'Cola offline', value: queues.length, color: '#b45309', bg: '#fef3e2' },
          ].map(s => (
            <div key={s.label} style={{ background: s.bg, borderRadius: 12, padding: '16px 18px' }}>
              <div style={{ fontFamily: 'Georgia,serif', fontSize: 32, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 12, color: s.color, fontWeight: 600 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#7a7060' }}>Cargando agentes...</div>
        ) : agents.length === 0 ? (
          <div style={{ background: '#fff', border: '1px solid #e2dbd0', borderRadius: 14, padding: 48, textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🤝</div>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: 18 }}>Sin agentes registrados</div>
          </div>
        ) : (
          <>
            {online.length > 0 && (
              <>
                <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#2d6a4f', marginBottom: 10 }}>
                  ● Online
                </div>
                {online.map((a, i) => <AgentCard key={i} agent={a} queues={queues} selected={selected} onSelect={setSelected} />)}
              </>
            )}
            {offline.length > 0 && (
              <>
                <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#7a7060', margin: '18px 0 10px' }}>
                  ○ Offline
                </div>
                {offline.map((a, i) => <AgentCard key={i} agent={a} queues={queues} selected={selected} onSelect={setSelected} />)}
              </>
            )}
          </>
        )}
      </div>

      {/* Agent detail */}
      {selected && (
        <div style={{ background: '#fff', border: '1px solid #e2dbd0', borderRadius: 14, padding: 22, height: 'fit-content', position: 'sticky', top: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 18 }}>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: 17 }}>Detalle agente</div>
            <button onClick={() => setSelected(null)} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 18, color: '#7a7060' }}>✕</button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
            <div style={{ width: 48, height: 48, borderRadius: '50%', background: selected.is_online ? '#eaf4ef' : '#f0ebe2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
              🤝
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>{selected.name || selected.id}</div>
              <div style={{ fontSize: 12, color: '#7a7060' }}>{selected.location || 'Sin ubicación'}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: selected.is_online ? '#2d6a4f' : '#b0a090' }} />
                <span style={{ fontSize: 11, fontWeight: 700, color: selected.is_online ? '#2d6a4f' : '#b0a090' }}>
                  {selected.is_online ? 'Online ahora' : `Offline · ${selected.last_seen ? new Date(selected.last_seen).toLocaleString('es-ES') : '—'}`}
                </span>
              </div>
            </div>
          </div>

          {[
            ['ID', selected.id],
            ['Teléfono', selected.phone || '—'],
            ['Email', selected.email || '—'],
            ['Zona cobertura', selected.coverage_zone || '—'],
            ['Capacidad diaria', selected.daily_capacity ? `$${selected.daily_capacity}` : '—'],
            ['Última actividad', selected.last_seen ? new Date(selected.last_seen).toLocaleString('es-ES') : '—'],
          ].map(([k, v]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0ebe2', fontSize: 13 }}>
              <span style={{ color: '#7a7060' }}>{k}</span>
              <span style={{ fontWeight: 600, fontFamily: k === 'ID' ? 'monospace' : 'inherit', fontSize: k === 'ID' ? 11 : 13 }}>{v}</span>
            </div>
          ))}

          {/* Offline queue */}
          <div style={{ marginTop: 18 }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#7a7060', marginBottom: 10 }}>
              Cola offline ({agentQueue.length})
            </div>
            {agentQueue.length === 0 ? (
              <div style={{ fontSize: 13, color: '#7a7060', textAlign: 'center', padding: '12px 0' }}>Sin órdenes en cola</div>
            ) : agentQueue.map((q, i) => (
              <div key={i} style={{ background: '#fef3e2', borderRadius: 8, padding: '10px 12px', marginBottom: 6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#c94a1e' }}>{q.ref}</span>
                  <span style={{ fontWeight: 700 }}>${q.amount_usd}</span>
                </div>
                <div style={{ fontSize: 12, color: '#7a7060', marginTop: 2 }}>{q.recipient_name}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AgentCard({ agent, queues, selected, onSelect }) {
  const agentQueue = queues.filter(q => q.agent_id === agent.id);
  const isSelected = selected?.id === agent.id;

  return (
    <div
      onClick={() => onSelect(isSelected ? null : agent)}
      style={{ background: '#fff', border: `1px solid ${isSelected ? '#c94a1e' : '#e2dbd0'}`, borderRadius: 12, padding: '16px 18px', marginBottom: 8, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      onMouseOver={e => { if (!isSelected) e.currentTarget.style.background = '#f9f6f1'; }}
      onMouseOut={e => { if (!isSelected) e.currentTarget.style.background = '#fff'; }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ position: 'relative' }}>
          <div style={{ width: 40, height: 40, borderRadius: '50%', background: agent.is_online ? '#eaf4ef' : '#f0ebe2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🤝</div>
          <div style={{ position: 'absolute', bottom: 0, right: 0, width: 10, height: 10, borderRadius: '50%', background: agent.is_online ? '#2d6a4f' : '#b0a090', border: '2px solid #fff' }} />
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>{agent.name || agent.id}</div>
          <div style={{ fontSize: 12, color: '#7a7060' }}>{agent.location || 'Sin ubicación'}</div>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {agentQueue.length > 0 && (
          <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: '#fef3e2', color: '#b45309' }}>
            {agentQueue.length} en cola
          </span>
        )}
        <span style={{ color: '#c94a1e', fontSize: 16 }}>→</span>
      </div>
    </div>
  );
}
